// CTSK-267 — shared search + filter + reset header for the registry
// table surfaces (DSR's Data Sources tab and the four Timeline Lane
// table tabs; CTSK-268/269 consume it next). One component so the
// header UX can't drift between registries: search box on the left,
// schema-appropriate filter dropdowns, optional schema-specific extra
// controls (date-range inputs etc. via children), then the action
// buttons (optional Add entry, always Reset).
//
// The component is deliberately stateless — every value/onChange pair
// is owned by the caller, because each table's filter set is keyed to
// its schema. Reset is a single callback: the CALLER decides what
// "clear everything" means for its surface (Timeline tables clear
// search + filters + sort; DSR clears search + filters, matching its
// pre-extraction behavior).

import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';

export interface FilterConfig {
  /** Placeholder option label, e.g. "All types". Doubles as the
   *  select's accessible name. */
  label: string;
  /** Currently selected option; '' = the "All …" placeholder. */
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}

export interface RegistryTableHeaderProps {
  searchValue: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder?: string;
  filters: FilterConfig[];
  onReset: () => void;
  /** If omitted, no add button renders (Timeline tables are read-only
   *  — their write path is the git round-trip / append endpoint). */
  addEntryLabel?: string;
  onAddEntry?: () => void;
  /** Extra schema-specific controls (e.g. the Events tab's BCE-aware
   *  year-range inputs) — rendered between the filter dropdowns and
   *  the action buttons. */
  children?: ReactNode;
}

export function RegistryTableHeader({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  filters,
  onReset,
  addEntryLabel,
  onAddEntry,
  children,
}: RegistryTableHeaderProps) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
      <input
        type="search"
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        style={{ flex: '1 1 240px', minWidth: 220, padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 6 }}
      />
      {filters.map((f) => (
        <select
          key={f.label}
          value={f.value}
          onChange={(e) => f.onChange(e.target.value)}
          aria-label={f.label}
          style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-raised)', minWidth: 140 }}
        >
          <option value="">{f.label}</option>
          {f.options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      ))}
      {children}
      {addEntryLabel && onAddEntry && (
        <button
          type="button"
          onClick={onAddEntry}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          <Plus size={12} /> {addEntryLabel}
        </button>
      )}
      <button
        type="button"
        onClick={onReset}
        style={{ padding: '6px 12px', background: 'var(--surface-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
      >
        Reset
      </button>
    </div>
  );
}
